import React, { useEffect, useState } from 'react'
import { createComponent } from '@ycos/fela'
import { getFontStyles } from '@ycos/primitives'
import { Picture } from '@ycos/picture'
import { USERINFO_STATUS } from '@ycos/constants'
import PropTypes from 'prop-types'

const LoyaltyPersonalShopperBox = createComponent(
  'LoyaltyPersonalShopperBox',
  () => ({
    maxWidth: '673px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    margin: '0 auto'
  }),
  'div'
)

const PersonalShopperPhoto = createComponent(
  'PersonalShopperPhoto',
  () => ({
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    overflow: 'hidden',
    'screen-medium': {
      width: '120px',
      height: '120px'
    }
  }),
  'div'
)

const PersonalShopperTitle = createComponent(
  'PersonalShopperTitle',
  ({ theme }) => ({
    ...getFontStyles(theme.typography, {
      type: theme.custom.loyaltyPersonalShopper.title.type,
      tagName: 'h4',
      name: theme.custom.loyaltyPersonalShopper.title.name
    }),
    fontSize: theme.custom.loyaltyPersonalShopper.title.fontSize,
    fontWeight: theme.custom.loyaltyPersonalShopper.title.fontWeight,
    lineHeight: theme.custom.loyaltyPersonalShopper.title.lineHeight,
    letterSpacing: theme.custom.loyaltyPersonalShopper.title.letterSpacing,
    color: theme.typography.colors.PrimaryBlack,
    marginTop: `${theme.spacingMultiplier * 3}px`
  }),
  'div'
)

const PersonalShopperName = createComponent(
  'PersonalShopperName',
  ({ theme }) => {
    const customStyle = theme.custom.loyaltyPersonalShopper.name
    return {
      ...getFontStyles(theme.typography, {
        type: customStyle.type,
        tagName: 'h3',
        name: customStyle.name
      }),
      marginTop: `${theme.spacingMultiplier}px`,
      fontSize: customStyle.fontSize,
      fontWeight: customStyle.fontWeight,
      lineHeight: customStyle.lineHeight,
      color: theme.typography.colors.PrimaryBlack,
      'screen-medium-max': customStyle.md
        ? {
          fontSize: customStyle.md.fontSize,
          lineHeight: customStyle.md.lineHeight,
          fontWeight: customStyle.md.fontWeight
        }
        : {}
    }
  },
  'div'
)

const PersonalShopperBio = createComponent(
  'PersonalShopperBio',
  ({ theme }) => ({
    ...getFontStyles(theme.typography, {
      type: theme.custom.loyaltyPersonalShopper.bio.type,
      tagName: 'p',
      name: theme.custom.loyaltyPersonalShopper.bio.name
    }),
    fontSize: theme.custom.loyaltyPersonalShopper.bio.fontSize,
    fontWeight: theme.custom.loyaltyPersonalShopper.bio.fontWeight,
    lineHeight: theme.custom.loyaltyPersonalShopper.bio.lineHeight,
    marginTop: `${theme.spacingMultiplier}px`,
    color: theme.typography.colors.PrimaryBlack,
    textAlign: 'center'
  }),
  'div'
)

const ContactBox = createComponent('ContactBox', ({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: `${theme.spacingMultiplier * 3}px`
}))

const PersonalShopperMobile = createComponent(
  'PersonalShopperMobile',
  ({ theme }) => ({
    ...getFontStyles(theme.typography, {
      type: theme.custom.loyaltyPersonalShopper.contact.type,
      tagName: 'p',
      name: theme.custom.loyaltyPersonalShopper.contact.name
    }),
    fontSize: theme.custom.loyaltyPersonalShopper.contact.fontSize,
    fontWeight: theme.custom.loyaltyPersonalShopper.contact.fontWeight,
    lineHeight: theme.custom.loyaltyPersonalShopper.contact.lineHeight,
    color: theme.typography.colors.PrimaryBlack,
    textDecoration: 'none'
  }),
  'a'
)

const PersonalShopperEmail = createComponent(
  'PersonalShopperEmail',
  ({ theme }) => ({
    ...getFontStyles(theme.typography, {
      type: theme.custom.loyaltyPersonalShopper.contact.type,
      tagName: 'p',
      name: theme.custom.loyaltyPersonalShopper.contact.name
    }),
    fontSize: theme.custom.loyaltyPersonalShopper.contact.fontSize,
    fontWeight: theme.custom.loyaltyPersonalShopper.contact.fontWeight,
    lineHeight: theme.custom.loyaltyPersonalShopper.contact.lineHeight,
    color: theme.typography.colors.PrimaryBlack,
    textDecoration: 'none'
  }),
  'a'
)

const DotDivider = createComponent(
  'DotDivider',
  ({ theme }) => ({
    width: '3px',
    height: '3px',
    backgroundColor: theme.typography.colors.SecondaryGreySteel,
    transform: 'rotate(-135deg)'
  }),
  'div'
)

const DotBox = createComponent(
  'DotBox',
  () => ({
    padding: '13px'
  }),
  'span'
)

// Custom hook to fetch user information using window.SF.getUserSummary()
// also add user contact info by  window.SF.getUserSummary()
// Becaue My account APP can not use @ycos/user-information
const useCustomUserInfo = () => {
  const [userInfo, setUserInfo] = useState(null)
  const [contactDetails, setContactDetails] = useState(null)
  const [userInfoStatus, setUserInfoStatus] = useState(USERINFO_STATUS.NOTLOADED)
  const [contactStatus, setContactStatus] = useState(USERINFO_STATUS.NOTLOADED)

  useEffect(() => {
    const fetchUserInfo = async () => {
      setUserInfoStatus(USERINFO_STATUS.LOADING)
      try {
        const result = await window?.SF?.getUserSummary()
        setUserInfo(result)
        setUserInfoStatus(USERINFO_STATUS.LOADED)
      } catch (error) {
        console.error('Error fetching user info:', error)
      }
    }

    const fetchContactDetails = async () => {
      setContactStatus(USERINFO_STATUS.LOADING)
      try {
        const result = await window?.SF?.getContactDetails()
        setContactDetails(result)
        setContactStatus(USERINFO_STATUS.LOADED)
      } catch (error) {
        console.error('Error fetching contact details:', error)
      }
    }

    fetchUserInfo()
    fetchContactDetails()
  }, [])

  return { userInfo, contactDetails, userInfoStatus, contactStatus }
}

// The defaultPersonalShopperInfo and psContact props are intended for Storybook and testing purposes.
// Alternatively, these props can be passed from a parent component if they are available globally.
const LoyaltyPersonalShopper = ({ item, personalShopperInfo, psContact, config, imageProps }) => {
  const { teaserTitle, teaserText, subTitle, fetchPriority, pictures } = item
  const [personalShopper, setPersonalShopper] = useState(personalShopperInfo || null)
  const [defaultPsContact, setDefaultPsContact] = useState(psContact || null)
  const { userInfo, contactDetails, userInfoStatus, contactStatus } = useCustomUserInfo()

  useEffect(() => {
    if (!personalShopper && userInfoStatus === USERINFO_STATUS.LOADED) {
      setPersonalShopper(userInfo?.personalShopperDetails)
    }
  }, [personalShopper, userInfo, userInfoStatus])

  useEffect(() => {
    if (!defaultPsContact && contactStatus === USERINFO_STATUS.LOADED) {
      setDefaultPsContact(contactDetails?.personalShopper)
    }
  }, [defaultPsContact, contactDetails, contactStatus])

  const { name, email, mobile, bio, imageURL } = personalShopper || {}
  const { email: defaultPsEmail, telephone: defaultPhone, name: defaultName } = defaultPsContact || {}

  const useDefaultPsdetails = !name || !email || !mobile
  const finalImage = !useDefaultPsdetails && imageURL ? [{ imageUrl: imageURL.replace('{{width}}', 200) }] : pictures
  const finalBio = useDefaultPsdetails ? teaserText : bio || teaserText
  const finalEmail = useDefaultPsdetails ? defaultPsEmail : email || defaultPsEmail
  const finalPhone = useDefaultPsdetails ? defaultPhone : mobile || defaultPhone
  const finalName = useDefaultPsdetails ? defaultName : name || defaultName

  return (
    <LoyaltyPersonalShopperBox>
      <PersonalShopperPhoto>
        <Picture fetchPriority={fetchPriority} images={finalImage} imagesphere={config?.imageSphere} {...imageProps} />
      </PersonalShopperPhoto>
      <PersonalShopperTitle>{teaserTitle}</PersonalShopperTitle>
      {finalName && <PersonalShopperName>{finalName}</PersonalShopperName>}
      <PersonalShopperBio>{finalBio}</PersonalShopperBio>
      <ContactBox>
        <PersonalShopperEmail href={`mailto:${finalEmail}`}>{subTitle}</PersonalShopperEmail>
        <DotBox>
          <DotDivider />
        </DotBox>
        <PersonalShopperMobile href={`tel:${finalPhone}`}>{finalPhone}</PersonalShopperMobile>
      </ContactBox>
    </LoyaltyPersonalShopperBox>
  )
}

LoyaltyPersonalShopper.layoutVariant = 'isc-loyalty-personal-shopper'

LoyaltyPersonalShopper.propTypes = {
  item: PropTypes.shape({
    subTitle: PropTypes.string,
    layoutVariant: PropTypes.string,
    teaserText: PropTypes.string,
    teaserTitle: PropTypes.string,
    pictures: PropTypes.array
  }),
  personalShopperInfo: PropTypes.shape({
    name: PropTypes.string,
    mobile: PropTypes.string,
    email: PropTypes.string,
    bio: PropTypes.string,
    imageURL: PropTypes.string
  }),
  config: PropTypes.shape({
    personalShopperImgTemplate: PropTypes.string,
    imagesphere: PropTypes.shape({
      quality: PropTypes.number
    })
  }),
  imageProps: PropTypes.shape({
    lazy: PropTypes.bool
  })
}

LoyaltyPersonalShopper.defaultProps = {
  imageProps: {
    lazy: false
  }
}

export default LoyaltyPersonalShopper
